import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, Menu, ChevronRight, Layers, CupSoda, Package } from "lucide-react";
import { CATEGORIES, ACCENTS } from "@/data/catalog";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const TICKER =
  "FREE PROOF ON ALL ORDERS • RUSH SERVICE AVAILABLE ON ELIGIBLE ORDERS • DIE-CUT PRECISION • CMYK CALIBRATED COLOR • ";

const Brand = () => (
  <Link to="/" data-testid="navbar-brand-logo" className="flex shrink-0 items-center py-2 lg:py-0">
    <img
      src="/assets/logo-dark.png"
      alt="TLF DESIGNZ — Print • Brand • Create"
      className="h-10 w-auto lg:h-11"
    />
  </Link>
);

const Navbar = () => {
  const { count, setOpen } = useCart();
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="overflow-hidden border-b border-white/10 bg-ink py-1.5">
        <div className="flex w-max animate-marquee-fast whitespace-nowrap">
          {[0, 1].map((n) => (
            <span key={n} className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
              {TICKER.repeat(4)}
            </span>
          ))}
        </div>
      </div>

      <div className="glass-header border-b border-white/10">
        <div className="mx-auto flex h-20 max-w-7xl items-center gap-4 px-4 sm:gap-6 sm:px-6 lg:h-16">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="navbar-mobile-menu-button"
                className="text-zinc-300 lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto border-white/10 bg-ink-2 p-0">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="border-b border-white/10 p-5">
                <Brand />
              </div>
              <nav className="p-5">
                {CATEGORIES.map((c) => (
                  <div key={c.slug} className="mb-4">
                    <button
                      data-testid={`mobile-nav-${c.slug}`}
                      onClick={() => {
                        setMobileOpen(false);
                        navigate(`/shop/${c.slug}`);
                      }}
                      className="flex w-full items-center justify-between font-display text-lg font-bold uppercase tracking-wide text-white"
                    >
                      {c.name}
                      <ChevronRight className="h-4 w-4 text-zinc-500" />
                    </button>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                      {c.subs.map((s) => (
                        <button
                          key={s.slug}
                          data-testid={`mobile-nav-${c.slug}-${s.slug}`}
                          onClick={() => {
                            setMobileOpen(false);
                            navigate(`/shop/${c.slug}/${s.slug}`);
                          }}
                          className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 hover:text-brand-cyan"
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-6 space-y-2 border-t border-white/10 pt-5">
                  <button
                    data-testid="mobile-nav-configurator"
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/builder");
                    }}
                    className="block font-display text-lg font-bold uppercase text-brand-cyan"
                  >
                    Print Builder
                  </button>
                  <button
                    data-testid="mobile-nav-bulk"
                    onClick={() => {
                      setMobileOpen(false);
                      navigate("/bulk-inquiry");
                    }}
                    className="block font-display text-lg font-bold uppercase text-brand-yellow"
                  >
                    Bulk Orders
                  </button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          <Brand />

          <nav className="hidden flex-1 items-center gap-1 lg:flex">
            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                data-testid="navbar-category-dropdown"
                className={`px-4 py-2 font-display text-sm font-bold uppercase tracking-widest transition-colors ${
                  megaOpen ? "text-brand-yellow" : "text-zinc-200 hover:text-brand-yellow"
                }`}
              >
                Shop All
              </button>
              <AnimatePresence>
                {megaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="fixed inset-x-0 top-[92px] border-b border-white/10 bg-ink-2/95 backdrop-blur-xl"
                  >
                    <div className="mx-auto grid max-w-7xl grid-cols-4 gap-x-8 gap-y-6 px-6 py-8">
                      {CATEGORIES.map((c) => {
                        const accent = ACCENTS[c.accent];
                        const Icon = c.icon;
                        return (
                          <div key={c.slug} className={`border-t-2 ${accent.border} pt-3`}>
                            <Link
                              to={`/shop/${c.slug}`}
                              data-testid={`mega-menu-${c.slug}`}
                              onClick={() => setMegaOpen(false)}
                              className="flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-white hover:text-brand-yellow"
                            >
                              <Icon className={`h-4 w-4 ${accent.text}`} />
                              {c.name}
                            </Link>
                            <div className="mt-2 space-y-1">
                              {c.subs.map((s) => (
                                <Link
                                  key={s.slug}
                                  to={`/shop/${c.slug}/${s.slug}`}
                                  data-testid={`mega-menu-${c.slug}-${s.slug}`}
                                  onClick={() => setMegaOpen(false)}
                                  className="block text-xs text-zinc-400 transition-colors hover:text-brand-cyan"
                                >
                                  {s.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      <div className="halftone flex flex-col justify-between border border-white/10 bg-ink-3 p-4">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                          Interactive Builder
                        </p>
                        <div className="space-y-2">
                          <Link
                            to="/configure/stickers-labels"
                            data-testid="mega-menu-builder-stickers"
                            onClick={() => setMegaOpen(false)}
                            className="flex items-center gap-2 font-display text-sm font-bold uppercase text-brand-magenta hover:underline"
                          >
                            <Layers className="h-4 w-4" /> Stickers & Labels
                          </Link>
                          <Link
                            to="/configure/snow-globe-tumblers"
                            data-testid="mega-menu-builder-tumblers"
                            onClick={() => setMegaOpen(false)}
                            className="flex items-center gap-2 font-display text-sm font-bold uppercase text-brand-cyan hover:underline"
                          >
                            <CupSoda className="h-4 w-4" /> Snow Globe Tumblers
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link
              to="/builder"
              data-testid="navbar-builder-link"
              className="px-4 py-2 font-display text-sm font-bold uppercase tracking-widest text-zinc-200 transition-colors hover:text-brand-cyan"
            >
              Print Builder
            </Link>
            <Link
              to="/track"
              data-testid="navbar-track-link"
              className="px-4 py-2 font-display text-sm font-bold uppercase tracking-widest text-zinc-200 transition-colors hover:text-brand-yellow"
            >
              Track Order
            </Link>
            <Link
              to="/bulk-inquiry"
              data-testid="navbar-bulk-link"
              className="px-4 py-2 font-display text-sm font-bold uppercase tracking-widest text-zinc-200 transition-colors hover:text-brand-magenta"
            >
              Bulk Orders
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => navigate("/bulk-inquiry")}
              data-testid="navbar-quick-order-button"
              className="hidden items-center gap-2 border border-brand-yellow/50 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-brand-yellow transition-colors hover:bg-brand-yellow hover:text-ink md:flex"
            >
              <Package className="h-3.5 w-3.5" /> Quick Order
            </button>
            <button
              data-testid="navbar-cart-button"
              onClick={() => setOpen(true)}
              className="relative flex items-center gap-2 border border-white/15 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-brand-cyan hover:text-brand-cyan"
            >
              <ShoppingCart className="h-4 w-4" />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span
                  data-testid="navbar-cart-count"
                  className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center bg-brand-magenta px-1 font-mono text-[10px] font-bold text-white"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
